<template>
  <div class="signup-simple-form">
    <h2 class="text-campton-bold">{{ $gettext('Start your free account now') }}</h2>
    <b-form @submit.prevent="submitSignup" class="gk-clear-form">

      <CountrySelector v-model="businesslocation" />

      <div>
        <b-form-group id="fieldset-signup-email" class="form-group floating-label">
          <b-form-input 
            type="email"
            id="input-signup-email"
            placeholder=" "
            class="floating-label" :class="{ 'is-touch': emailTouch }"
            v-model="email"
            :state="emailState"
            trim required
            @blur="emailTouch = true"
          />
          <label class="floating-label">{{ $gettext('Email address') }}</label>
          <small v-if="showEmailWarning || (emailTouch && !emailState)" class="email-hint form-text">
            <transition name="fade" appear mode="out-in">
              <span v-if="showEmailWarning" :key="showEmailWarning" class="text-danger">
                <font-awesome-icon icon="fa-regular fa-warning" />{{ emailWarningTxt }}
              </span>
              <span v-else-if="emailTouch && !emailState" :key="showEmailWarning" class="text-danger">
                <font-awesome-icon icon="fa-regular fa-circle-xmark" />{{ $gettext('Please enter a valid email address') }}
              </span>
            </transition>
          </small>
        </b-form-group>
      </div>

      <b-form-group id="fieldset-signup-password" class="form-group floating-label">
        <div class="position-relative">
          <b-form-input
            type="password"
            id="input-signup-password"
            placeholder=" "
            class="floating-label" :class="{ 'is-touch': passwordTouch }"
            v-model="password"
            :state="passwordState"
            trim required
            @blur="passwordTouch = true"
          />
          <label class="floating-label">{{ $gettext('Password') }}</label>
          <ShowPasswordIcon input-id="input-signup-password" />
        </div>
        <PasswordStrengthHint v-if="showPasswordHint" :password="password" @score-changed="(score) => { passwordScore = score }" />
      </b-form-group>

      <div style="padding-top: 0.5rem; width: 100%;">
        <GekkoButton
          type="submit"
          :text="$gettext('Get started')"
          :loading="loadingData || savingData"
          :disabled="(passwordScore < 1)"
          variant="primary"
          class="w-100 text-campton-book position-relative"
          size="xl"
        />
      </div>
    </b-form>

    <div class="gk-divider">
      <div class="divider-line"></div>
      <div class="divider-text">{{ $gettext('or') }}</div>
      <div class="divider-line"></div>
    </div>
    <GoogleSSOButton 
      ref="googleSSOButton"
      @country-not-set="$emit('show-country', false)" 
      @authenticatedWithGoogle="handleThirdPartyAuth"
      :businesslocation="null" 
      :preferredlanguage="preferredlanguage"
    />
    <!-- <AppleSSOButton 
      ref="appleSSOButton"
      @country-not-set="$emit('show-country', false)" 
      @authenticatedWithApple="handleThirdPartyAuth"
      :businesslocation="null" 
      :preferredlanguage="preferredlanguage"
    /> -->
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex"
import { randomCelebName, randomEmail } from "@/utils/"
import GoogleSSOButton from "@/components/auth/GoogleSSOButton.vue"
import AppleSSOButton from "@/components/auth/AppleSSOButton.vue"
import GekkoButton from "@/components/GekkoButton"
import ShowPasswordIcon from "@/components/ui/ShowPasswordIcon"
import PasswordStrengthHint from "@/components/ui/PasswordStrengthHint"
import CountrySelector from "@/components/signup/CountrySelector"

export default {
  name: 'SignupFormSimple',
  components: {
    GekkoButton,
    ShowPasswordIcon,
    PasswordStrengthHint,
    GoogleSSOButton,
    AppleSSOButton,
    CountrySelector
  },
  props: {
    inviteCode: {
      type: String,
      default: null
    },
  },
  data () {
    return {
      email: '',
      businesslocation: '',
      preferredlanguage: '',
      password: '',
      showEmailWarning: false,
      emailWarningTxt: '',
      passwordScore: 0,
      signupStep: 1,
      loading: false,
      loadingCountries: false,
      loadingUserLocation: false,
      savingData: false,
      emailTouch: false,
      passwordTouch: false
    }
  },
  computed: {
    ...mapGetters({
      userCountry: 'signup/getUserCountry',
      userLanguage: 'signup/getUserLanguage',
    }),
    randomPlaceholderName () {
      return randomCelebName()
    },
    randomPlaceholderEmail () {
      return randomEmail(this.randomPlaceholderName)
    },

    loadingData () {
      return this.loading || this.loadingCountries || this.loadingUserLocation
    },
    showPasswordHint () {
      return (this.password.length > 0)
    },
    emailState () {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(this.email) && !this.showEmailWarning
    },
    passwordState () {
      return (this.passwordScore >= 1)
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
  },
  methods: {
    ...mapActions({ 
      saveData: 'signup/signUp',
      saveDataHeroes: 'signup/signUpHeroes',
      checkAvailability: 'signup/checkAvailability',
      loadCountries: 'main/loadCountries',
      geoLocateUserIp: 'signup/geoLocateUserIp',
      getAccessToken: "auth/getAccessToken"
    }),
    ...mapMutations({
      setNewSignup: 'signup/SET_NEW_SIGNUP'
    }),
    setupData () {
      console.log('this.$route.params.language', this.$route.params.language)
      // geo locate user
      this.loadingUserLocation = true
      this.geoLocateUserIp().then(() => {
        this.preferredlanguage = this.$route.params.language || this.userLanguage
        this.businesslocation = this.userCountry
        this.loadingUserLocation = false
      })
    },
    submitSignup () {
      this.loading = true
      return this.checkAvailability(this.email).then((response) => {
        this.showEmailWarning = false
        console.log('response', response)
        if (response.status === 200) {
          if (this.passwordScore > 0) {
            const data = {
              email: this.email,
              password: this.password,
              country: this.businesslocation,
              preferred_language: this.preferredlanguage
            }
            this.savingData = true
            if (this.inviteCode) {
              this.saveDataHeroes({ data: data, invite_code: this.inviteCode }).then(() => {
                console.log('saved data, trying login')
                this.login()
              }).catch((error) => {
                console.log('error', error)
              }).finally(() => {
                this.savingData = false
              })
            } else {
              this.saveData(data).then(() => {
                console.log('saved data, trying login')
                this.login()
              }).catch((error) => {
                console.log('error', error)
              }).finally(() => {
                this.savingData = false
              })
            }
          } else {
            console.log('bad password')
          }
        }
      }).catch((error) => {
        if (error.status === 409) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('This email address already exists')
        } else if (error.status === 422) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('That email doesn\'t seem to be ok')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    login () {
      this.loading = true
      this.getAccessToken({
        'username': this.email,
        'password': this.password,
      }).then((response) => {
        console.log('Login.vue::login() -> getAccessToken THEN response', response)
        this.$emit('authorised', false)
      }).catch((error) => {
        console.log('error authenticating new user', error)
      }).finally(() => {
        this.loading = false
      })
    },
    handleThirdPartyAuth(is_authenticated) {
      this.loading = false
      if (is_authenticated) {
        this.$emit('authorised', false)
      } else {
        this.savingData = false
        // TODO make error message field for google sso??
        this.showEmailWarning = true
        this.emailWarningTxt = this.$gettext('Third party authentication failed.')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.signup-simple-form {
  .email-hint {
    span {
      .svg-inline--fa {
        margin-right: 0.25rem
      }
    }
  }

  .form-control {
    &:not(.is-touch) {
      &.is-invalid, &.is-valid, &.is-invalid:focus, &.is-valid:focus {
        border: 1px solid #dfdfdf;
      }
    }
    &.is-touch {
      &.is-invalid {
        border: 1px solid $colorDanger;
      }
    }
  }
}
</style>